import { useCallback, useMemo } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import {
  IPrioritySchedulerColumnData,
  MiIcon,
  MiIconButton,
  PrioritySchedulerBoard,
} from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  IUserListItemResponse,
} from "@miplatform/types";
import { getDateDay, isSame } from "@miplatform/utils";
import { mdiArrowCollapse, mdiArrowExpand, mdiClose } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { useCalendarContext } from "../../../providers";
import {
  NHC_ASSIGNMENT_BOARD_DAY_COUNT,
  type StagedWorkOrderAssignment,
} from "../NHCPriorityScheduler/useAssignmentStaging";

const VISIBLE_COLUMN_COUNT = 3;

interface AssignableUserColumnProps {
  user: IUserListItemResponse;
  selectedDate: Moment;
  selected?: boolean;
  columnWidth: number;
  hourHeight: number;
  zoom?: number;
  onClose?: () => void;
  boardExpanded: boolean;
  onBoardExpandedChange: (expanded: boolean) => void;
  stagedAssignments?: StagedWorkOrderAssignment[];
  assignedEvents?: IAttemptListItemResponseForWorkOrder[];
  onRemoveStagedAttempt?: (entityId: string) => void;
}

const AssignableUserColumn = ({
  user,
  selectedDate,
  selected = false,
  columnWidth,
  hourHeight,
  onClose,
  boardExpanded,
  onBoardExpandedChange,
  stagedAssignments = [],
  assignedEvents = [],
  onRemoveStagedAttempt,
}: Readonly<AssignableUserColumnProps>) => {
  const { classes, cx } = useStyles();

  const { calendarSet } = useCalendarContext();

  const toggleExpanded = useCallback(() => {
    onBoardExpandedChange(!boardExpanded);
  }, [boardExpanded, onBoardExpandedChange]);

  const allColumns = useMemo<IPrioritySchedulerColumnData[]>(() => {
    const start = selectedDate.clone().startOf("day");
    return Array.from({ length: NHC_ASSIGNMENT_BOARD_DAY_COUNT }, (_, i) => {
      const day = start.clone().add(i, "days");
      const dayAssignedEvents = assignedEvents.filter((e) => {
        const sd = e.scheduledStartDate ?? e.startDate ?? e.scheduleDate;
        if (!sd) {
          return false;
        }
        return isSame({ date1: sd, date2: day.toDate(), timeUnit: "day" });
      });
      const dayStagedEvents = stagedAssignments
        .filter((s) => s.dayOffset === i)
        .map((s) => s.attempt);
      return {
        id: `${user.id}-column-${i}`,
        user,
        date: day,
        dateView: true,
        events: [...dayAssignedEvents, ...dayStagedEvents],
        columnHeaderHeight: 0,
      };
    });
  }, [user, selectedDate.valueOf(), stagedAssignments, assignedEvents]);

  const visibleColumns = useMemo(() => {
    if (boardExpanded) {
      return allColumns;
    }
    const [first] = allColumns;
    if (!first) {
      return allColumns;
    }
    return [
      {
        ...first,
        events: [...assignedEvents, ...stagedAssignments.map((s) => s.attempt)],
      },
    ];
  }, [allColumns, boardExpanded, stagedAssignments, assignedEvents]);

  const displayName =
    user.displayName || user.name || `${user.firstName} ${user.lastName}`;

  return (
    <Box className={cx(classes.column, selected && classes.columnSelected)}>
      <Box className={cx(classes.header, selected && classes.headerSelected)}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          noWrap
          className={classes.userName}
        >
          {displayName}
        </Typography>
        <Box className={classes.actions}>
          <MiIconButton onClick={toggleExpanded} data-testid="expand-button">
            <MiIcon
              path={boardExpanded ? mdiArrowCollapse : mdiArrowExpand}
              size={0.75}
            />
          </MiIconButton>
          <MiIconButton onClick={onClose} data-testid="close-button">
            <MiIcon path={mdiClose} size={0.75} />
          </MiIconButton>
        </Box>
      </Box>
      <Box
        className={cx(classes.body, boardExpanded && classes.bodyExpanded)}
        sx={
          boardExpanded && columnWidth
            ? { maxWidth: columnWidth * VISIBLE_COLUMN_COUNT }
            : undefined
        }
      >
        <PrioritySchedulerBoard
          columns={visibleColumns}
          columnWidth={columnWidth}
          hourHeight={hourHeight}
          date={getDateDay(selectedDate)}
          onEventRemove={onRemoveStagedAttempt}
          calendarSet={calendarSet?.config ?? null}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  column: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden",
    gap: theme.spacing(2),
  },
  columnSelected: {
    borderColor: theme.palette.primary.main,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.grey.light,
    height: 34,
  },
  headerSelected: {
    backgroundColor: theme.palette.primary.light,
  },
  userName: {
    flex: 1,
    fontWeight: theme.fontWeight.semiBold,
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  body: {
    flex: 1,
    padding: theme.spacing(1),
    overflow: "auto",
  },
  bodyExpanded: {
    padding: theme.spacing(1, 0),
  },
}));

export default AssignableUserColumn;
